import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Newspaper, FileText, FileWarning, Image as ImageIcon, ArrowRight, Activity } from 'lucide-react';
import { adminApi } from './api';
import { useLanguage } from '../context/LanguageContext';
import { ADMIN_T } from './labels';

const StatCard = ({ Icon, label, value, accent }) => (
  <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 shadow-sm">
    <div className="flex items-center gap-3">
      <span className={`w-11 h-11 rounded-xl flex items-center justify-center text-white shadow ${accent}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</p>
        <p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
      </div>
    </div>
  </div>
);

const AdminOverview = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = ADMIN_T[language] || ADMIN_T.en;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    adminApi.get('/overview').then(({ data }) => {
      if (active) setStats(data);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  return (
    <div className="space-y-6">
      <div className={isAr ? 'text-right' : 'text-left'}>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">{t.dashboard}</h1>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard Icon={Newspaper}   label={t.total + ' • ' + t.news}   value={stats?.news_total ?? '—'}   accent="bg-gradient-to-br from-brand-blue to-brand-blue-700" />
        <StatCard Icon={FileText}    label={t.published}                 value={stats?.news_published ?? '—'} accent="bg-gradient-to-br from-emerald-500 to-brand-green" />
        <StatCard Icon={FileWarning} label={t.draft}                     value={stats?.news_draft ?? '—'} accent="bg-gradient-to-br from-amber-500 to-orange-500" />
        <StatCard Icon={ImageIcon}   label={t.mediaFiles}                value={stats?.media_total ?? '—'} accent="bg-gradient-to-br from-sky-500 to-brand-blue-600" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent news */}
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <div className={`flex items-center justify-between mb-4 ${isAr ? 'flex-row-reverse' : ''}`}>
            <h2 className="text-base font-bold text-slate-900 dark:text-white">{t.recentNews}</h2>
            <Link to="/admin/news" className="text-xs font-semibold text-brand-blue-700 dark:text-cyan-300 hover:underline inline-flex items-center gap-1">
              {t.news} <ArrowRight className={`w-3 h-3 ${isAr ? 'rotate-180' : ''}`} />
            </Link>
          </div>
          {loading ? (
            <div className="text-sm text-slate-500">…</div>
          ) : stats?.recent_news?.length ? (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {stats.recent_news.map((n) => (
                <li key={n.id} className={`py-3 flex items-center gap-3 ${isAr ? 'flex-row-reverse text-right' : ''}`}>
                  <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded-full ${
                    n.status === 'published'
                      ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                      : 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300'
                  }`}>{n.status}</span>
                  <Link to={`/admin/news/${n.id}`} className="flex-1 min-w-0 truncate text-sm text-slate-700 dark:text-slate-200 hover:text-brand-blue-700 dark:hover:text-cyan-300">
                    {isAr ? n.title_ar || n.title_en || '—' : n.title_en || n.title_ar || '—'}
                  </Link>
                  <span className="text-[11px] font-mono text-slate-500">{(n.updated_at || '').slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">{t.noData}</p>
          )}
        </div>

        {/* Activity */}
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <div className={`flex items-center gap-2 mb-4 ${isAr ? 'flex-row-reverse' : ''}`}>
            <Activity className="w-4 h-4 text-brand-blue" />
            <h2 className="text-base font-bold text-slate-900 dark:text-white">{t.recentActivity}</h2>
          </div>
          {loading ? (
            <div className="text-sm text-slate-500">…</div>
          ) : stats?.recent_activity?.length ? (
            <ul className="space-y-2 text-sm">
              {stats.recent_activity.map((a) => (
                <li key={a.id} className={`flex items-center gap-3 ${isAr ? 'flex-row-reverse text-right' : ''}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-blue flex-shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-slate-700 dark:text-slate-200">{a.summary}</span>
                  <span className="text-[11px] font-mono text-slate-500">{(a.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">{t.noData}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
